import { TGlobalStore } from 'store'
import type { StoreApi } from 'zustand'
import { FindingsType } from 'models/user.model'

export interface PaginationState {
  currentPage: number
  totalRecords: number
  totalPages: number
  recordsPerPage: number
}

export interface FiltersState {
  searchValue: string
  departmentsFilter: string | null
  threatFilter: string | null
  webDomainFilter: string | null
  issueFilter: string | null
  cardLeakFilter: string | null
  dateFoundFilter: string | null
  viewMode: 'issue' | 'source'
}

interface FindingsPaginationState {
  [FindingsType.ALL]: PaginationState
  [FindingsType.EXTERNAL]: PaginationState
  [FindingsType.INTERNAL]: PaginationState
}

interface FindingsFiltersState {
  [FindingsType.ALL]: FiltersState
  [FindingsType.EXTERNAL]: FiltersState
  [FindingsType.INTERNAL]: FiltersState
}

interface FindingsPrevFiltersState {
  [FindingsType.ALL]: Record<string, any>
  [FindingsType.EXTERNAL]: Record<string, any>
  [FindingsType.INTERNAL]: Record<string, any>
}

export interface IFindingsState {
  paginationState: FindingsPaginationState
  filtersState: FindingsFiltersState
  prevFiltersState: FindingsPrevFiltersState
  setPaginationState: (findingsType: FindingsType, pagination: Partial<PaginationState>) => void
  setFiltersState: (findingsType: FindingsType, filters: Partial<FiltersState>) => void
  setPrevFiltersState: (findingsType: FindingsType, filters: Record<string, any>) => void
  resetFindingsPagination: (findingsType?: FindingsType) => void
  resetFindingsFilters: (findingsType?: FindingsType) => void
}

const defaultPagination: PaginationState = {
  currentPage: 1,
  totalRecords: 0,
  totalPages: 0,
  recordsPerPage: 10,
}

const defaultFilters: FiltersState = {
  searchValue: '',
  departmentsFilter: null,
  threatFilter: null,
  webDomainFilter: 'ALL',
  issueFilter: null,
  cardLeakFilter: null,
  dateFoundFilter: null,
  viewMode: 'issue',
}

const findingsSlice = (set: StoreApi<TGlobalStore>['setState']): IFindingsState => ({
  paginationState: {
    [FindingsType.ALL]: { ...defaultPagination },
    [FindingsType.EXTERNAL]: { ...defaultPagination },
    [FindingsType.INTERNAL]: { ...defaultPagination },
  },
  filtersState: {
    [FindingsType.ALL]: { ...defaultFilters },
    [FindingsType.EXTERNAL]: { ...defaultFilters },
    [FindingsType.INTERNAL]: { ...defaultFilters },
  },
  prevFiltersState: {
    [FindingsType.ALL]: {},
    [FindingsType.EXTERNAL]: {},
    [FindingsType.INTERNAL]: {},
  },
  setPaginationState: (findingsType, pagination) =>
    set(state => ({
      ...state,
      paginationState: {
        ...state.paginationState,
        [findingsType]: {
          ...state.paginationState[findingsType],
          ...pagination,
        },
      },
    })),
  setFiltersState: (findingsType, filters) =>
    set(state => ({
      ...state,
      filtersState: {
        ...state.filtersState,
        [findingsType]: {
          ...state.filtersState[findingsType],
          ...filters,
        },
      },
    })),
  setPrevFiltersState: (findingsType, filters) =>
    set(state => ({
      ...state,
      prevFiltersState: {
        ...state.prevFiltersState,
        [findingsType]: filters,
      },
    })),
  resetFindingsPagination: (findingsType?: FindingsType) =>
    set(state => ({
      ...state,
      paginationState: findingsType
        ? {
            ...state.paginationState,
            [findingsType]: { ...defaultPagination },
          }
        : {
            [FindingsType.ALL]: { ...defaultPagination },
            [FindingsType.EXTERNAL]: { ...defaultPagination },
            [FindingsType.INTERNAL]: { ...defaultPagination },
          },
      prevFiltersState: findingsType
        ? {
            ...state.prevFiltersState,
            [findingsType]: {},
          }
        : {
            [FindingsType.ALL]: {},
            [FindingsType.EXTERNAL]: {},
            [FindingsType.INTERNAL]: {},
          },
    })),
  resetFindingsFilters: (findingsType?: FindingsType) =>
    set(state => ({
      ...state,
      filtersState: findingsType
        ? {
            ...state.filtersState,
            [findingsType]: { ...defaultFilters },
          }
        : {
            [FindingsType.ALL]: { ...defaultFilters },
            [FindingsType.EXTERNAL]: { ...defaultFilters },
            [FindingsType.INTERNAL]: { ...defaultFilters },
          },
    })),
})

export default findingsSlice
